import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Star, BookOpen, Plus, ArrowRight, Award, ExternalLink, Coins, Clock, Lightbulb } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

function getClubToken() { return localStorage.getItem('club_token'); }
function setClubAuth(token, name, email) {
  localStorage.setItem('club_token', token);
  localStorage.setItem('club_name', name);
  localStorage.setItem('club_email', email);
}

export default function ClubDashboard() {
  const [token, setToken] = useState(getClubToken());
  const [tab, setTab] = useState('review');
  const [profile, setProfile] = useState(null);
  const [books, setBooks] = useState([]);
  const [myBooks, setMyBooks] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auth form
  const [authMode, setAuthMode] = useState('join');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMsg, setAuthMsg] = useState('');

  // Book submit form
  const [showSubmit, setShowSubmit] = useState(false);
  const [bookForm, setBookForm] = useState({ title: '', author: '', amazon_url: '', genre: '', description: '', credits_per_review: 10 });

  // Review submit
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewBookId, setReviewBookId] = useState(null);

  const headers = { 'X-Club-Token': token || '' };

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [profileRes, booksRes, myBooksRes, myReviewsRes] = await Promise.all([
        api.get('/club/profile', { headers }),
        api.get('/club/books', { headers }),
        api.get('/club/books/mine', { headers }),
        api.get('/club/reviews/mine', { headers }),
      ]);
      setProfile(profileRes.data);
      setBooks(booksRes.data);
      setMyBooks(myBooksRes.data);
      setMyReviews(myReviewsRes.data);
    } catch {
      localStorage.removeItem('club_token');
      setToken(null);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMsg('');
    try {
      const endpoint = authMode === 'join' ? '/club/join' : '/club/login';
      const { data } = await api.post(endpoint, { name, email });
      const t = data.access_token;
      setClubAuth(t, name, email);
      setToken(t);
      setAuthMsg(data.message || 'Welcome!');
    } catch (err) {
      setAuthMsg(err.response?.data?.detail || 'Error');
    } finally { setAuthLoading(false); }
  };

  const handleSubmitBook = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/club/books/submit', bookForm, { headers });
      setShowSubmit(false);
      setBookForm({ title: '', author: '', amazon_url: '', genre: '', description: '', credits_per_review: 10 });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error submitting book');
    } finally { setLoading(false); }
  };

  const handleSubmitReview = async (bookId) => {
    if (!reviewUrl.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/club/reviews/submit', { book_id: bookId, amazon_review_url: reviewUrl }, { headers });
      setReviewBookId(null);
      setReviewUrl('');
      setError('');
      alert(`Review verified! You earned ${data.credits_earned} credits.`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error submitting review');
    } finally { setLoading(false); }
  };

  // Not logged in
  if (!token) {
    return (
      <>
        <SeoHead title="Join The Authors' Review Exchange — Free Book Review Club" noindex={true} />
        <section className="pt-28 pb-20 md:pt-40 md:pb-32">
          <div className="max-w-md mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-3" data-testid="club-auth-title">
                {authMode === 'join' ? <>Join <span className="text-primary">The Exchange</span></> : <>Welcome <span className="text-primary">Back</span></>}
              </h1>
              <p className="text-sm text-muted-foreground">
                {authMode === 'join' ? 'Sign up in seconds. No password needed.' : 'Enter your name and email to access your dashboard.'}
              </p>
            </div>
            <form onSubmit={handleAuth} className="space-y-4" data-testid="club-auth-form">
              <input
                type="text" placeholder="Your Name" value={name} onChange={e => setName(e.target.value)} required
                data-testid="club-name-input"
                className="w-full h-12 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none"
              />
              <input
                type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} required
                data-testid="club-email-input"
                className="w-full h-12 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none"
              />
              <button type="submit" disabled={authLoading} data-testid="club-auth-submit"
                className="w-full h-12 rounded-sm bg-primary text-primary-foreground font-semibold hover:bg-white hover:text-black transition-all shadow-[0_0_15px_rgba(212,175,55,0.3)]">
                {authLoading ? 'Processing...' : authMode === 'join' ? 'Join Free — Get 50 Credits' : 'Access Dashboard'}
              </button>
            </form>
            {authMsg && <p className="text-sm text-center mt-4 text-primary" data-testid="club-auth-msg">{authMsg}</p>}
            <p className="text-xs text-center text-muted-foreground mt-6">
              {authMode === 'join' ? (
                <>Already a member? <button onClick={() => setAuthMode('login')} className="text-primary hover:underline">Sign in</button></>
              ) : (
                <>New here? <button onClick={() => setAuthMode('join')} className="text-primary hover:underline">Join free</button></>
              )}
            </p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead title="Dashboard — The Authors' Review Exchange" noindex={true} />
      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">

          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl tracking-tight" data-testid="club-dash-title">
                Welcome, <span className="text-primary">{profile?.name || '...'}</span>
              </h1>
              {profile?.badge && (
                <span className="inline-flex items-center gap-1 text-xs font-bold mt-1" style={{ color: profile.badge.color }} data-testid="club-badge">
                  <Award className="w-3 h-3" /> {profile.badge.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center" data-testid="club-credits">
                <p className="font-serif text-2xl text-primary">{profile?.credits ?? '—'}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Credits</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-2xl">{profile?.total_reviews ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Reviews</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-2xl">{profile?.total_books ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Books</p>
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-red-400 mb-4 bg-red-500/10 border border-red-500/20 rounded-sm p-3" data-testid="club-error">{error}</p>}

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto" data-testid="club-tabs">
            {[
              { id: 'review', label: 'Books to Review', icon: Star },
              { id: 'mybooks', label: 'My Books', icon: BookOpen },
              { id: 'myreviews', label: 'My Reviews', icon: Clock },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`club-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {/* Review Queue Tab */}
          {tab === 'review' && (
            <div>
              {books.length === 0 ? (
                <div className="text-center py-16 bg-white/5 border border-white/10 rounded-sm" data-testid="club-empty-books">
                  <BookOpen className="w-10 h-10 text-primary/30 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">No books in the review queue yet.</p>
                  <p className="text-xs text-muted-foreground">Be the first to submit a book!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {books.filter(b => b.member_email !== profile?.email).map(book => (
                    <div key={book.id} className="bg-white/5 border border-white/10 rounded-sm p-5" data-testid={`club-book-${book.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-serif text-lg">{book.title}</h3>
                          <p className="text-xs text-muted-foreground">by {book.author} &middot; {book.genre}</p>
                        </div>
                        <span className="flex items-center gap-1 text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-sm">
                          <Coins className="w-3 h-3" /> +{book.credits_per_review}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">{book.description}</p>
                      <div className="flex items-center gap-3">
                        <a href={book.amazon_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                          View on Amazon <ExternalLink className="w-3 h-3" />
                        </a>
                        {reviewBookId === book.id ? (
                          <div className="flex-1 flex gap-2">
                            <input type="text" placeholder="Paste your Amazon review URL" value={reviewUrl} onChange={e => setReviewUrl(e.target.value)}
                              className="flex-1 h-9 px-3 text-xs bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none"
                              data-testid="review-url-input" />
                            <button onClick={() => handleSubmitReview(book.id)} disabled={loading}
                              data-testid="review-submit-btn"
                              className="h-9 px-4 text-xs font-semibold bg-primary text-primary-foreground rounded-sm hover:bg-white hover:text-black transition-all">
                              {loading ? '...' : 'Submit'}
                            </button>
                            <button onClick={() => { setReviewBookId(null); setReviewUrl(''); }}
                              className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setReviewBookId(book.id)}
                            data-testid={`review-btn-${book.id}`}
                            className="text-xs font-semibold border border-white/20 rounded-sm px-4 py-2 hover:border-primary hover:text-primary transition-all flex items-center gap-1">
                            <Star className="w-3 h-3" /> I Reviewed This
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* My Books Tab */}
          {tab === 'mybooks' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-muted-foreground">{myBooks.length} book{myBooks.length !== 1 ? 's' : ''} submitted</p>
                <button onClick={() => setShowSubmit(!showSubmit)} data-testid="club-submit-book-btn"
                  className="rounded-sm px-5 py-2.5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-white hover:text-black transition-all flex items-center gap-1.5 shadow-[0_0_10px_rgba(212,175,55,0.2)]">
                  <Plus className="w-3.5 h-3.5" /> Submit a Book
                </button>
              </div>

              {showSubmit && (
                <form onSubmit={handleSubmitBook} className="bg-white/5 border border-primary/20 rounded-sm p-6 mb-6 space-y-4" data-testid="club-book-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" placeholder="Book Title" value={bookForm.title} onChange={e => setBookForm({...bookForm, title: e.target.value})} required
                      data-testid="book-title-input"
                      className="h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm" />
                    <input type="text" placeholder="Author Name" value={bookForm.author} onChange={e => setBookForm({...bookForm, author: e.target.value})} required
                      data-testid="book-author-input"
                      className="h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm" />
                  </div>
                  <input type="url" placeholder="Amazon Book URL" value={bookForm.amazon_url} onChange={e => setBookForm({...bookForm, amazon_url: e.target.value})} required
                    data-testid="book-url-input"
                    className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <select value={bookForm.genre} onChange={e => setBookForm({...bookForm, genre: e.target.value})} required
                      data-testid="book-genre-select"
                      className="h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground focus:border-primary outline-none text-sm">
                      <option value="">Select Genre</option>
                      {['Fiction', 'Non-Fiction', 'Mystery', 'Romance', 'Sci-Fi', 'Fantasy', 'Thriller', 'Biography', 'Self-Help', 'Business', 'History', 'Poetry', 'Children', 'Young Adult', 'Horror', 'Other'].map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-3">
                      <label className="text-xs text-muted-foreground whitespace-nowrap">Credits per review:</label>
                      <input type="number" min="5" max="50" value={bookForm.credits_per_review} onChange={e => setBookForm({...bookForm, credits_per_review: parseInt(e.target.value) || 10})}
                        data-testid="book-credits-input"
                        className="h-11 w-24 px-3 bg-white/5 border border-white/10 rounded-sm text-foreground focus:border-primary outline-none text-sm text-center" />
                    </div>
                  </div>
                  <textarea placeholder="Brief description of your book..." value={bookForm.description} onChange={e => setBookForm({...bookForm, description: e.target.value})} required rows={3}
                    data-testid="book-desc-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm resize-none" />
                  <button type="submit" disabled={loading} data-testid="book-submit-btn"
                    className="rounded-sm px-6 py-3 text-sm font-semibold bg-primary text-primary-foreground hover:bg-white hover:text-black transition-all">
                    {loading ? 'Submitting...' : 'Submit Book for Reviews'}
                  </button>
                </form>
              )}

              {myBooks.length === 0 && !showSubmit ? (
                <div className="text-center py-16 bg-white/5 border border-white/10 rounded-sm">
                  <BookOpen className="w-10 h-10 text-primary/30 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">You haven't submitted any books yet.</p>
                  <button onClick={() => setShowSubmit(true)} className="text-sm text-primary hover:underline mt-2">Submit your first book</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myBooks.map(book => (
                    <div key={book.id} className="bg-white/5 border border-white/10 rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`mybook-${book.id}`}>
                      <div>
                        <h3 className="font-serif text-base">{book.title}</h3>
                        <p className="text-xs text-muted-foreground">{book.genre} &middot; {book.reviews_received} review{book.reviews_received !== 1 ? 's' : ''} received &middot; {book.credits_per_review} credits/review</p>
                      </div>
                      <a href={book.amazon_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 shrink-0">
                        Amazon <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* My Reviews Tab */}
          {tab === 'myreviews' && (
            <div>
              {myReviews.length === 0 ? (
                <div className="text-center py-16 bg-white/5 border border-white/10 rounded-sm">
                  <Star className="w-10 h-10 text-primary/30 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">You haven't reviewed any books yet.</p>
                  <button onClick={() => setTab('review')} className="text-sm text-primary hover:underline mt-2">Browse books to review</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myReviews.map(rev => (
                    <div key={rev.id} className="bg-white/5 border border-white/10 rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`myreview-${rev.id}`}>
                      <div>
                        <h3 className="font-serif text-base">{rev.book_title}</h3>
                        <p className="text-xs text-muted-foreground">
                          <span className="text-green-400 font-semibold">+{rev.credits_awarded} credits</span> &middot; {new Date(rev.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <a href={rev.amazon_review_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 shrink-0">
                        View Review <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/club/leaderboard" className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="club-leaderboard-link">
              <Award className="w-3 h-3" /> View Leaderboard
            </Link>
            <Link to="/club/growth-hub" className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="club-growth-hub-link">
              <Lightbulb className="w-3 h-3" /> Growth Hub
            </Link>
            <Link to="/club" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> About The Exchange
            </Link>
            <button onClick={() => { localStorage.removeItem('club_token'); setToken(null); }} className="text-xs text-muted-foreground hover:text-foreground ml-auto" data-testid="club-logout">
              Sign Out
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
